"use client";

import { PROCESS_INSTANCE_QUERY } from "@/src/shared/const";
import { useContextStore } from "@/src/stores/useContextStore";
import { useProcessInstanceStore } from "@/src/stores/useProcessInstanceStore";
import { MutationOptions, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "next/navigation";
import {
    apiCallsSideEffects,
    isStopCallsError,
    throwStopCallsError,
} from "./sideEffects";
import {
    apiCallsModalStore,
    closeLoadingModal,
    openLoadingModal,
} from "./modalStore";
import { useFormContext } from "react-hook-form";
import { apiCallsParamsMap } from "./contants";
import { Action, ActionMethod, ApiCallModalType } from "./types";
import { getContextVariablesToUpdate } from "./utils";
import {
    useApiCall,
    useNextNavigation,
    useSpecificStepNavigation,
} from "@/src/components/Action/useNavigation";
import {useAnalytics} from "@/src/components/pages/StepPage/components/useAnalytics";
import { useFormDataMiddleware } from "@/src/components/Action/useFormDataMiddleware";
import { getContextVariablesToApiCall } from "@/src/components/Action/utils";
import { useKeysToUpdate } from "@/src/components/Action/useKeysToUpdate";
import { useTimeSpentStore } from "@/src/stores/useTimeSpentStore";

export const useHandleApiCall = (
    apiCall = false,
    mutateOptions?: MutationOptions<any, any, any>,
) => {
    const { callComponentEvent } = useAnalytics();
    const setProcessInstance = useProcessInstanceStore((state) => state.setProcessInstance);
    const { replaceVariablesObject} = useContextStore()
    const queryClient = useQueryClient();
    const { formDataMiddleware } = useFormDataMiddleware();

    const { mutateAsync: call } = useApiCall(
        {
            onSuccess: (data, variables, context) => {
                console.log("useHandleApiCall", {
                    data,
                    variables,
                    context,
                    apiCall,
                });
                if (variables?.actionName === "importBill") {
                    return;
                }
                if (apiCall) {
                    queryClient.invalidateQueries({
                        queryKey: ["processInstance", processInstanceId],
                    });
                } else{
                    replaceVariablesObject(data.contextInstance.variables);
                    setProcessInstance(data);
                }
                mutateOptions?.onSuccess?.(data, variables, context);
            },
            onMutate: mutateOptions?.onMutate,
        },
        apiCall,
    );
    const { getValues } = useFormContext();

    const getVariable = useContextStore((state) => state.getVariable);
    const searchParams = useSearchParams();

    const processInstanceId = searchParams.get(PROCESS_INSTANCE_QUERY);

    const processInstance = useProcessInstanceStore(
        (state) => state.processInstance,
    );
    const {
        formState: { dirtyFields, errors },
    } = useFormContext();

    const { getKeysToUpdate } = useKeysToUpdate();

    const handleApiCall = (
        formData: any,
        params: {
            actionName: string;
            actionParams: any[];
            mapper: Record<string, string | null>;
        },
    ) => {
        const { actionName, actionParams, mapper } = params;
        if (processInstanceId && processInstance.id) {
            const recordData = formDataMiddleware(null, formData);

            return call({
                actionName,
                body: {
                    id: processInstance.id,
                    processId: processInstance.processId,
                    activeStepId: processInstance.activeStepId,
                    contextInstance: {
                        id: processInstance.contextInstance.id,
                        variables: getContextVariablesToApiCall({
                            formData: recordData,
                            params: actionParams,
                            mapper,
                            keyToUpdate: getKeysToUpdate(),
                        }),
                    },
                },
            });
        }
    };

    const handleApiCallWithSideEffects = async (
        formData: any = {},
        params: {
            actionName: string;
            actionParams: any[];
            mapper: Record<string, string | null>;
        },
        {
            onSuccessData,
            onErrorData,
            onLoading,
            withLoader,
        }: {
            onSuccessData?: any;
            onErrorData?: any;
            onLoading?: { title: string; description: string, disableClose?: boolean };
            withLoader?: boolean;
        } = {},
    ) => {
        const { actionName, actionParams, mapper } = params;
        const sideEffects =
            apiCallsSideEffects[actionName as keyof typeof apiCallsSideEffects];
        if (processInstanceId && processInstance) {
            try {
                withLoader && openLoadingModal(onLoading);
                const recordData = formDataMiddleware(null, formData);
                const response = await call({
                    actionName,
                    body: {
                        id: processInstance.id,
                        processId: processInstance.processId,
                        activeStepId: processInstance.activeStepId,
                        contextInstance: {
                            id: processInstance.contextInstance.id,
                            variables: getContextVariablesToApiCall({
                                formData: recordData,
                                params: actionParams,
                                mapper,
                                keyToUpdate: getKeysToUpdate(),
                            }),
                        },
                    },
                });
                sideEffects?.onSuccess?.(onSuccessData, response, callComponentEvent);
                if (withLoader) {
                    closeLoadingModal();
                }

                return response;
            } catch (e) {
                if (isStopCallsError(e)) {
                    closeLoadingModal();
                }

                sideEffects?.onError?.(onErrorData, formData, e);
                console.log(e);
                throwStopCallsError();
            }

            closeLoadingModal();
        }
    };

    const handleApiCallByName = async (
        actionName: string,
        withoutSideEffect = true,
        {
            additionalParams,
            formDataOverride,
            paramsOverride,
            mapper = {},
        }: {
            additionalParams?: { onSuccessData?: any; onErrorData?: any };
            formDataOverride?: any;
            paramsOverride?: any;
            mapper?: any;
        } = {},
    ): Promise<any> => {
        const params =
            paramsOverride ||
            (apiCallsParamsMap[
                actionName as keyof typeof apiCallsParamsMap
            ] as any);
        const formData = formDataOverride || formDataMiddleware(null, getValues());
        if (withoutSideEffect) {
            return await handleApiCall(formData, {
                actionName,
                actionParams: params,
                mapper: mapper,
            });
        } else {
            return await handleApiCallWithSideEffects(
                // maybe should be catched
                formData,
                {
                    actionName,
                    actionParams: params,
                    mapper: mapper,
                },
                additionalParams,
            );
        }
    };

    const { getTimeSpent } = useTimeSpentStore();
    const { mutateAsync: mutateSpecificStep } =
        useSpecificStepNavigation(mutateOptions);
    const { mutateAsync: mutateNextStep } = useNextNavigation(mutateOptions);
    const goToStep = async (stepId: string) => {
        if (!processInstanceId) {
            return;
        }

        return await mutateSpecificStep({
            code: stepId,
            body: {
                processInstanceId,
                contextVariablesToUpdate: getContextVariablesToUpdate(
                    getValues(),
                    dirtyFields,
                ),
                timeSpent: getTimeSpent(),
                navigationType: "NEXT",
            },
        });
    };

    const goNext = async () => {
        if (!processInstanceId) {
            return;
        }

        const payload = {
            processInstanceId,
            contextVariablesToUpdate: getContextVariablesToUpdate(
                getValues(),
                dirtyFields,
            ),
            timeSpent: getTimeSpent(),
        };
        return await mutateNextStep(payload);
    };

    const { open } = apiCallsModalStore();

    const handleModalOpen = (
        modalName: ApiCallModalType,
        additionalParams: any,
    ) => {
        open(modalName, additionalParams);
    };

    const handleOpenLink = (link: string, target: string) => {
        setTimeout(() => window.open(link, target), 0);
    };
    const handleApiCall2 = () => {};
    const updateBEContext = async () => {
        // тут буде форс сет контексту з форми
    };

    // TODO
    const runAction = async (action: Action) => {
        const { method, params, additionalParams } = action;
        await updateBEContext();

        switch (method) {
            case ActionMethod.open_modal:
                return handleModalOpen(params.modalType, additionalParams);
            case ActionMethod.apicall:
                return handleApiCall2();

            case ActionMethod.open_link:
                handleOpenLink(params.href, params.target);
                return;
            case ActionMethod.apicall_rating:
                return;
            case ActionMethod.navigation_next:
                return goNext();
            case ActionMethod.navigation_prev:
                return;
            case ActionMethod.navigation_step:
                return goToStep(params.stepId);
            case ActionMethod.viber:
            case ActionMethod.letter:
            case ActionMethod.contacts:
            default:
                break;
        }
    };

    return {
        handleApiCall,
        handleApiCallWithSideEffects,
        handleApiCallByName,
        runAction,
    };
};
